import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import AffiliateCard from "@/components/affiliates/AffiliateCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { AFFILIATE_CATEGORIES } from "@/lib/affiliates";

const AffiliatesDirectory = () => {
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get("category") ?? "all";
  const [search, setSearch] = useState("");

  const { data: affiliates, isLoading } = useQuery({
    queryKey: ["affiliates", "directory"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliates_public" as any)
        .select("*")
        .eq("status", "active")
        .order("featured", { ascending: false })
        .order("name");
      if (error) throw error;
      return data as any[];
    },
  });

  const filtered = useMemo(() => {
    if (!affiliates) return [];
    const q = search.trim().toLowerCase();
    return affiliates.filter((a) => {
      if (activeCategory !== "all" && a.category !== activeCategory) return false;
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        a.tagline?.toLowerCase().includes(q) ||
        a.services?.some((s) => s.toLowerCase().includes(q)) ||
        a.specialties?.some((s) => s.toLowerCase().includes(q))
      );
    });
  }, [affiliates, activeCategory, search]);

  const setCategory = (value: string) => {
    const next = new URLSearchParams(params);
    if (value === "all") next.delete("category");
    else next.set("category", value);
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      <section className="pt-36 pb-10">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">
              Affiliate directory
            </span>
            <h1 className="font-display text-3xl md:text-4xl font-bold mt-2 mb-3">
              Find a provider for your next project
            </h1>
            <p className="text-muted-foreground">
              Browse vetted freelance specialists and production partners ready to support
              funded work across Alabama.
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-3 mt-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, service, or specialty"
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={activeCategory === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setCategory("all")}
              >
                All
              </Button>
              {AFFILIATE_CATEGORIES.map((c) => (
                <Button
                  key={c.value}
                  variant={activeCategory === c.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCategory(c.value)}
                >
                  {c.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto px-6">
          {isLoading ? (
            <div className="text-center text-muted-foreground py-16">Loading providers…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-xl bg-card/50">
              <p className="text-muted-foreground">
                No providers match yet. Try a different filter, or check back soon.
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((a, i) => (
                <AffiliateCard key={a.id} affiliate={a} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default AffiliatesDirectory;